import React, { useState, useEffect } from "react";
import { fetchPost } from "../../../services/serviceClient";
import { ToastType, useToast } from "../../../../../../context_providers/toast/toast_ctx";
import axios from "axios";
import { fetchPut } from "../../../../../../service/api_client";

interface CaseStudyData {
  _id: string;
  id: string;
  title: string;
  description: string;
  image_url: string;
  file_url: string;
  published_date: string;
  status: string;
  created_at: string;
  updated_at: string;
  deleted: boolean;
  alt_text?: string;
  category?: string;
}

interface EditCaseStudyFormProps {
  isOpen: boolean;
  onClose: () => void;
  caseStudyId: string;
  onUpdate: () => void;
}

const EditCaseStudyForm: React.FC<EditCaseStudyFormProps> = ({
  isOpen,
  onClose,
  caseStudyId,
  onUpdate,
}) => {
  const [formData, setFormData] = useState<{
    title: string;
    description: string;
    featured_image: File | null;
    alt_text: string;
    category: string;
    upload_file: File | null;
    publish_date: string | null;
    existing_image_url: string;
    existing_file_url: string;
  }>({
    title: "",
    description: "",
    featured_image: null,
    alt_text: "",
    category: "",
    upload_file: null,
    publish_date: null,
    existing_image_url: "",
    existing_file_url: "",
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { showToast } = useToast();

  // Fetch case study data when modal opens
  useEffect(() => {
    if (isOpen && caseStudyId) {
      fetchCaseStudyData();
    }
  }, [isOpen, caseStudyId]);

const fetchCaseStudyData = async () => {
  setIsLoading(true);

  try {
    const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/case-study/get-case-study/${caseStudyId}`;
    const res = await axios.get(url, {
      headers: {
        "client-id": process.env.REACT_APP_MARKETING_API_CLIENT_ID,
        "client-key": process.env.REACT_APP_MARKETING_API_CLIENT_KEY,
      },
    });

    console.log("Fetched case study data:", res.data);

    // Check for HTTP success and valid body
    if (res.status === 200 && res.data) {
      // Assuming your API wraps data in “data” property
      const data: CaseStudyData = res.data.data;

      // Guard if “data” is undefined or malformed
      if (!data) {
        throw new Error("Response data is null or undefined");
      }

      setFormData({
        title: data.title || "",
        description: data.description || "",
        featured_image: null,
        alt_text: data.alt_text || "",
        category: data.category || "",
        upload_file: null,
        publish_date: data.published_date || null,
        existing_image_url: data.image_url || "",
        existing_file_url: data.file_url || "",
      });
    } else {
      // Non-200 or missing data case
      showToast({
        type: ToastType.error,
        text: (res.data && (res.data.error || res.data.message)) || "Error fetching case study data",
      });
    }
  } catch (error) {
    console.error("Error fetching case study:", error);

    // More detailed error message if available
    let msg = "Error fetching case study data";
    if (axios.isAxiosError && axios.isAxiosError(error)) {
      if (error.response && error.response.data) {
        const respData = error.response.data as any;
        msg = respData.error || respData.message || msg;
      }
    }

    showToast({
      type: ToastType.error,
      text: msg,
    });
  } finally {
    setIsLoading(false);
  }
};

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData((prev) => ({
        ...prev,
        [e.target.name]: file,
      }));
      if (errors[e.target.name]) {
        setErrors((prev) => ({
          ...prev,
          [e.target.name]: "",
        }));
      }
    }
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.title.trim()) {
      newErrors.title = "Title is required";
    }

    if (!formData.description.trim()) {
      newErrors.description = "Description is required";
    }

    // For edit, image is not required if existing image exists
    if (!formData.featured_image && !formData.existing_image_url) {
      newErrors.featured_image = "Featured image is required";
    } else if (formData.featured_image && !formData.featured_image?.type.startsWith("image/")) {
      newErrors.featured_image = "Please select a valid image file";
    }

    if (!formData.alt_text.trim()) {
      newErrors.alt_text = "Alt text for image is required";
    }

    if (!formData.category.trim()) {
      newErrors.category = "Category is required";
    }

    // For edit, file is not required if existing file exists
    if (!formData.upload_file && !formData.existing_file_url) {
      newErrors.upload_file = "Upload file is required";
    } else if (formData.upload_file) {
      const allowedTypes = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ];
      if (!allowedTypes.includes(formData.upload_file?.type || "")) {
        newErrors.upload_file = "Please upload a PDF or DOC file";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      await handleUpdate();
      showToast({
        type: ToastType.success,
        text: "Case Study updated successfully!",
      });
      onUpdate(); // Refresh the list
      onClose(); // Close modal
    } catch (error) {
      console.error("Error updating case study:", error);
      showToast({
        type: ToastType.error,
        text: "Error updating case study. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdate = async () => {
    // Create FormData for file upload with correct field names for backend
    const formDataToSubmit = new FormData();
    formDataToSubmit.append("title", formData.title);
    formDataToSubmit.append("description", formData.description);
    formDataToSubmit.append("alt_text", formData.alt_text);
    formDataToSubmit.append("category", formData.category);
    formDataToSubmit.append("published_date", new Date().toISOString());
    
    // Only append files if new ones are selected
    if (formData.featured_image) {
      formDataToSubmit.append("image", formData.featured_image);
    }
    if (formData.upload_file) {
      formDataToSubmit.append("file", formData.upload_file);
    }

    console.log("FormData being sent to update API:");
    for (let pair of formDataToSubmit.entries()) {
      console.log(pair[0] + ': ' + pair[1]);
    }

    const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/case-study/update/${caseStudyId}`;
    const res = await fetchPut(url, formDataToSubmit);
    
    if (!res.success) {
      throw new Error(res.error || "API call failed");
    }
    
    return res.data;
  };

  const handleClose = () => {
    // Reset form when closing
    setFormData({
      title: "",
      description: "",
      featured_image: null,
      alt_text: "",
      category: "",
      upload_file: null,
      publish_date: null,
      existing_image_url: "",
      existing_file_url: "",
    });
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className=" overflow-y-scroll border border-gray-300 rounded-lg fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[85vh] overflow-hidden">
        {/* Modal Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 bg-gray-50">
          <h2 className="text-xl font-semibold text-gray-900">
            Edit Case Study
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1 hover:bg-gray-200 rounded"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Loading State */}
        {isLoading ? (
          <div className="p-12 text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-3 text-gray-600">Loading case study data...</p>
          </div>
        ) : (
          <>
            {/* Modal Content */}
            <div className="overflow-y-auto max-h-[calc(85vh-120px)]">
              <div className="p-6 space-y-4">
                {/* Title Field */}
                <div>
                  <label
                    htmlFor="title"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                      errors.title ? "border-red-500" : "border-gray-300"
                    }`}
                    placeholder="Enter case study title"
                  />
                  {errors.title && (
                    <p className="mt-1 text-sm text-red-500">{errors.title}</p>
                  )}
                </div>

                {/* Description Field */}
                <div>
                  <label
                    htmlFor="description"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Description <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={3}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none ${
                      errors.description ? "border-red-500" : "border-gray-300"
                    }`}
                    placeholder="Enter case study description"
                  />
                  {errors.description && (
                    <p className="mt-1 text-sm text-red-500">{errors.description}</p>
                  )}
                </div>

                {/* Two Column Layout for Alt Text and Category */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Alt Text Field */}
                  <div>
                    <label
                      htmlFor="alt_text"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Image Alt Text <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="alt_text"
                      name="alt_text"
                      value={formData.alt_text}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                        errors.alt_text ? "border-red-500" : "border-gray-300"
                      }`}
                      placeholder="Enter alt text"
                    />
                    {errors.alt_text && (
                      <p className="mt-1 text-sm text-red-500">{errors.alt_text}</p>
                    )}
                  </div>

                  {/* Category Field */}
                  <div>
                    <label
                      htmlFor="category"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Category <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="category"
                      name="category"
                      value={formData.category}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                        errors.category ? "border-red-500" : "border-gray-300"
                      }`}
                      placeholder="e.g., Fleet, CPOs"
                    />
                    {errors.category && (
                      <p className="mt-1 text-sm text-red-500">{errors.category}</p>
                    )}
                  </div>
                </div>

                {/* Featured Image Field */}
                <div>
                  <label
                    htmlFor="featured_image"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Featured Image
                  </label>
                  {formData.existing_image_url && (
                    <div className="mb-3 p-3 bg-gray-50 rounded-md">
                      <p className="text-sm text-gray-600 mb-2">Current image:</p>
                      <img
                        src={formData.existing_image_url}
                        alt="Current featured image"
                        className="w-24 h-16 object-cover rounded border shadow-sm"
                      />
                    </div>
                  )}
                  <input
                    type="file"
                    id="featured_image"
                    name="featured_image"
                    onChange={handleFileChange}
                    accept="image/*"
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:text-sm file:bg-blue-50 file:text-blue-600 hover:file:bg-blue-100 ${
                      errors.featured_image ? "border-red-500" : "border-gray-300"
                    }`}
                  />
                  {errors.featured_image && (
                    <p className="mt-1 text-sm text-red-500">
                      {errors.featured_image}
                    </p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    Leave blank to keep current image, or upload new one to replace
                  </p>
                </div>

                {/* Upload File Field */}
                <div>
                  <label
                    htmlFor="upload_file"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Upload File (PDF/DOC)
                  </label>
                  {formData.existing_file_url && (
                    <div className="mb-3 p-3 bg-gray-50 rounded-md">
                      <p className="text-sm text-gray-600 mb-2">Current file:</p>
                      <a
                        href={formData.existing_file_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-blue-600 hover:text-blue-800 text-sm font-medium"
                      >
                        <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                        </svg>
                        View current file
                      </a>
                    </div>
                  )}
                  <input
                    type="file"
                    id="upload_file"
                    name="upload_file"
                    onChange={handleFileChange}
                    accept=".pdf,.doc,.docx"
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:text-sm file:bg-blue-50 file:text-blue-600 hover:file:bg-blue-100 ${
                      errors.upload_file ? "border-red-500" : "border-gray-300"
                    }`}
                  />
                  {errors.upload_file && (
                    <p className="mt-1 text-sm text-red-500">
                      {errors.upload_file}
                    </p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    Leave blank to keep current file, or upload new one to replace
                  </p>
                </div>
              </div>
            </div>

            {/* Modal Footer - Fixed at bottom */}
            <div className="flex justify-end space-x-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={isSubmitting}
                onClick={handleSubmit}
                className={`px-6 py-2 rounded-md font-medium text-white transition-colors ${
                  isSubmitting
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                }`}
              >
                {isSubmitting ? (
                  <span className="flex items-center">
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Updating...
                  </span>
                ) : (
                  "Update Case Study"
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default EditCaseStudyForm;